import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '@progress/kendo-react-buttons';
import { Tooltip } from '@progress/kendo-react-tooltip';
import { stickyNoteIcon } from '@progress/kendo-svg-icons';
import type { SVGIcon } from '@progress/kendo-svg-icons';
import { useLocalization } from '@progress/kendo-react-intl';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { enMessages } from '../../../../../messages/en-US';
import useIdentityStore from '../../../../../Zustand/identityStore';
import notesApiService from '../../Utilities/NotesApiService';
import {
  EntityNoteInput,
  EntityNotesPanelItem,
  EntityNotesPanelItemProps,
  EntityType,
} from './EntityNotesPanel';

interface Props {
  entityType: EntityType;
  entityId: string;
  entityName?: string;
  /** Optional serial number — rendered in parentheses next to entityName in the drawer header. */
  entitySerial?: string;
  /** Icon for the trigger button. Defaults to the sticky-note icon. */
  svgIcon?: SVGIcon;
  /** Overrides the drawer header icon (e.g. a criticality chevron for alerts/notifications). */
  headerIcon?: React.ReactNode;
  /** Kendo Button props for context-specific styling. */
  buttonProps?: {
    fillMode?: 'solid' | 'flat' | 'outline' | 'link';
    size?: 'small' | 'medium' | 'large';
    themeColor?: 'base' | 'primary';
    className?: string;
  };
}

/** Query key shared by all hooks/components that fetch this entity's notes. */
export const entityNotesQueryKey = (entityType: EntityType, entityId: string) =>
  ['entityNotes', entityType, entityId] as const;

export default function EntityNotesButton({
  entityType,
  entityId,
  entityName,
  entitySerial,
  svgIcon = stickyNoteIcon,
  headerIcon,
  buttonProps,
}: Props) {
  const localizationService = useLocalization();
  const queryClient = useQueryClient();
  // Identity — read from the store so each consumer doesn't need to pass it.
  const currentUserId = useIdentityStore((x) => x.user?.profile?.sub) || '';
  const currentUserName = useIdentityStore((x) => x.user?.profile?.name) || '';

  const [open, setOpen] = useState(false);

  // Lock background page scroll while the drawer is open.
  useEffect(() => {
    if (!open) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  // Only fetch when the drawer is open — keeps Gateway/GreasePump table rows lazy.
  const notesQuery = useQuery(
    entityNotesQueryKey(entityType, entityId),
    () => notesApiService.getNotesForEntity(entityType, entityId),
    {
      enabled: open && !!entityId,
      staleTime: 30 * 1000,
    },
  );

  const invalidate = () => {
    queryClient.invalidateQueries(entityNotesQueryKey(entityType, entityId));
    // Also refresh the asset-wide Notes tab, which fetches under a separate key.
    queryClient.invalidateQueries(['assetAllNotes']);
  };

  const addNoteMutation = useMutation(
    (input: EntityNoteInput) =>
      notesApiService.createNote(entityType, entityId, {
        comment: input.description,
        categories: input.categories,
        isInternalOnly: input.isInternalOnly,
      }),
    { onSuccess: invalidate },
  );

  const editNoteMutation = useMutation(
    ({ noteId, input }: { noteId: string; input: EntityNoteInput }) =>
      notesApiService.updateNote(noteId, {
        comment: input.description,
        categories: input.categories,
        isInternalOnly: input.isInternalOnly,
      }),
    { onSuccess: invalidate },
  );

  const deleteNoteMutation = useMutation(
    (noteId: string) => notesApiService.deleteNote(noteId),
    { onSuccess: invalidate },
  );

  const drawerItems: EntityNotesPanelItemProps[] = open
    ? [{
        entityType,
        entityId,
        entityName,
        entitySerial,
        headerIcon,
        notes: notesQuery.data ?? [],
        noteCount: notesQuery.data?.length ?? 0,
        currentUserId,
        currentUserName,
        onAdd: (input) => addNoteMutation.mutate(input),
        onEdit: (noteId, input) => editNoteMutation.mutate({ noteId, input }),
        onDelete: (noteId) => deleteNoteMutation.mutate(noteId),
        onClose: () => setOpen(false),
      }]
    : [];

  return (
    <>
      <Tooltip anchorElement="target" position="bottom" parentTitle={true}>
        <Button
          svgIcon={svgIcon}
          onClick={() => setOpen(true)}
          title={localizationService.toLanguageString('custom.notes', enMessages.custom.notes)}
          fillMode={buttonProps?.fillMode}
          size={buttonProps?.size}
          themeColor={buttonProps?.themeColor}
          className={buttonProps?.className}
        />
      </Tooltip>
      {open && createPortal(
        // Above the global `.k-animation-container { z-index: 20003 }` so the drawer sits over Kendo
        // Popovers (e.g. the dashboard alerts popover that hosts this button). The drawer's own
        // category dropdowns are lifted higher still via `.notes-drawer-popup` in index.scss.
        <>
          <div
            className="fixed inset-0"
            style={{ background: 'rgba(0,0,0,0.15)', zIndex: 20005 }}
            onClick={() => setOpen(false)}
          />
          <div
            className="fixed top-0 right-0 h-full shadow-xl"
            style={{ width: 'min(100vw, 750px)', zIndex: 20006 }}
          >
            <EntityNotesPanelItem {...drawerItems[0]} />
          </div>
        </>,
        document.body
      )}
    </>
  );
}
